<template>
  <section class="panel site-image-panel">
    <div class="panel-title image-title-row">
      <span class="image-title">현장 정보 이미지</span>

      <div class="image-title-actions" v-if="normalizedSiteImages.length > 1">
        <span class="image-counter">
          {{ currentImageIndex + 1 }} / {{ normalizedSiteImages.length }}
        </span>
      </div>
    </div>

    <div v-if="normalizedSiteImages.length > 0" class="site-image-slider">
      <button
        v-if="normalizedSiteImages.length > 1"
        class="slide-btn prev-btn"
        @click="prevImage"
        type="button"
      >
        <i class="fa-solid fa-chevron-left"></i>
      </button>

      <div class="image-wrapper">
        <img
          v-for="(img, index) in normalizedSiteImages"
          :key="img.attachFileNo || img.fileUrl || index"
          :src="img.fileUrl"
          :alt="img.originalFileName || `현장 이미지 ${index + 1}`"
          class="site-image"
          :class="{ active: index === currentImageIndex }"
          v-show="index === currentImageIndex"
          @click="openImageViewer(index)"
        />
      </div>

      <button
        v-if="normalizedSiteImages.length > 1"
        class="slide-btn next-btn"
        @click="nextImage"
        type="button"
      >
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>

    <div v-else class="image-empty">
      현장 이미지가 없습니다.
    </div>

    <div v-if="normalizedSiteImages.length > 1" class="slide-indicators">
      <button
        v-for="(img, index) in normalizedSiteImages"
        :key="`indicator-${img.attachFileNo || img.fileUrl || index}`"
        class="indicator"
        :class="{ active: index === currentImageIndex }"
        @click="goToImage(index)"
        type="button"
      />
    </div>
  </section>

  <Teleport to="body">
    <div
      v-if="isImageViewerOpen"
      class="image-viewer-overlay"
      @click.self="closeImageViewer"
    >
      <button class="viewer-close" @click="closeImageViewer" type="button">
        <i class="fa-solid fa-xmark"></i>
      </button>

      <button class="viewer-btn left" @click.stop="prevImage" type="button">
        <i class="fa-solid fa-chevron-left"></i>
      </button>

      <img
        :src="normalizedSiteImages[currentImageIndex]?.fileUrl"
        class="viewer-image"
        alt="현장 이미지 확대"
      />

      <button class="viewer-btn right" @click.stop="nextImage" type="button">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: 'ImagePanel',

  props: {
    siteImages: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      currentImageIndex: 0,
      slideTimer: null,
      normalizedSiteImages: [],
      isImageViewerOpen: false
    }
  },

  watch: {
    siteImages: {
      immediate: true,
      deep: true,
      async handler() {
        this.currentImageIndex = 0
        await this.rebuildSiteImages()
        this.restartAutoSlide()
      }
    }
  },

  beforeUnmount() {
    this.clearSlideTimer()
    this.unlockBodyScroll()
  },

  methods: {
    clearSlideTimer() {
      if (this.slideTimer) {
        clearInterval(this.slideTimer)
        this.slideTimer = null
      }
    },

    buildFileUrl(image) {
      if (!image) return ''

      if (image.fileUrl) return image.fileUrl
      if (image.previewUrl) return image.previewUrl
      if (image.attachFileNo) return `/app/file/image/${image.attachFileNo}`
      if (image.id) return `/app/file/image/${image.id}`

      return ''
    },

    async rebuildSiteImages() {
      const rawImages = Array.isArray(this.siteImages) ? this.siteImages : []

      const normalized = rawImages
        .filter(item => !item?.deleted)
        .map((item, index) => {
          return {
            ...item,
            sortOrder: item?.sortOrder ?? index + 1,
            fileUrl: this.buildFileUrl(item)
          }
        })
        .filter(item => !!item.fileUrl)
        .sort((a, b) => {
          const aOrder = Number(a.sortOrder || 0)
          const bOrder = Number(b.sortOrder || 0)
          return aOrder - bOrder
        })

      if (!normalized.length) {
        this.normalizedSiteImages = []
        return
      }

      const preloadResults = await Promise.all(
        normalized.map(item => {
          return new Promise(resolve => {
            const img = new Image()

            img.onload = () => {
              resolve({
                ok: true,
                item
              })
            }

            img.onerror = () => {
              console.error('현장 이미지 preload 실패:', item.fileUrl)
              resolve({
                ok: false,
                item
              })
            }

            img.src = item.fileUrl
          })
        })
      )

      this.normalizedSiteImages = preloadResults
        .filter(result => result.ok)
        .map(result => result.item)

      if (this.currentImageIndex >= this.normalizedSiteImages.length) {
        this.currentImageIndex = 0
      }
    },

    prevImage() {
      if (!this.normalizedSiteImages.length) return

      this.currentImageIndex =
        this.currentImageIndex === 0
          ? this.normalizedSiteImages.length - 1
          : this.currentImageIndex - 1

      this.restartAutoSlide()
    },

    nextImage() {
      if (!this.normalizedSiteImages.length) return

      this.currentImageIndex =
        this.currentImageIndex === this.normalizedSiteImages.length - 1
          ? 0
          : this.currentImageIndex + 1
    },

    goToImage(index) {
      this.currentImageIndex = index
      this.restartAutoSlide()
    },

    startAutoSlide() {
      this.clearSlideTimer()

      if (this.normalizedSiteImages.length <= 1) return

      this.slideTimer = setInterval(() => {
        this.nextImage()
      }, 3000)
    },

    restartAutoSlide() {
      this.clearSlideTimer()
      this.startAutoSlide()
    },

    lockBodyScroll() {
      document.body.style.overflow = 'hidden'
    },

    unlockBodyScroll() {
      document.body.style.overflow = ''
    },

    openImageViewer(index) {
      this.currentImageIndex = index
      this.isImageViewerOpen = true
      this.lockBodyScroll()
    },

    closeImageViewer() {
      this.isImageViewerOpen = false
      this.unlockBodyScroll()
    }
  }
}
</script>

<style scoped>
.panel {
  background: #ffffff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.panel-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-main);
}

.image-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.image-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-main);
}

.image-title-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.image-counter {
  font-size: 12px;
  color: var(--color-text-sub);
  line-height: 1;
}

.site-image-slider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.image-wrapper {
  flex: 1;
  min-width: 0;
  height: 190px;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f5f8;
  border: 1px solid #e5e7eb;
  position: relative;
}

.site-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  cursor: zoom-in;
}

.image-empty {
  margin-top: 12px;
  font-size: 13px;
  color: var(--color-text-sub);
  text-align: center;
  padding: 20px 10px;
  border: 1px dashed #d7dde5;
  border-radius: 10px;
  background: #fafbfd;
}

.slide-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d9dfe7;
  border-radius: 999px;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.slide-btn:hover {
  background-color: #f2f5f9;
  border-color: #c7d0db;
}

.slide-indicators {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  border: none;
  background: #d1d5db;
  cursor: pointer;
  padding: 0;
}

.indicator.active {
  background: var(--color-primary);
}

@media (max-width: 1200px) {
  .image-wrapper {
    height: 170px;
  }
}

.image-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.viewer-image {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}

.viewer-close {
  position: absolute;
  top: 20px;
  right: 30px;
  font-size: 28px;
  color: #fff;
  background: transparent;
  border: none;
  cursor: pointer;
}

.viewer-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 20px;
  text-align: center;
}

.viewer-btn.left {
  left: 30px;
}

.viewer-btn.right {
  right: 30px;
}

.viewer-btn:hover {
  background: rgba(255,255,255,0.3);
}
</style>